import React from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { I18n } from '@aws-amplify/core';
import { Col } from 'antd';
import moment from 'moment';
import { useGetDataExtractionCSVToDownload } from '@/hooks';
import { downloadFile } from '@/utils';
import { ORIGINAL_STATUS_COMPLETED } from '@/context/DataExtraction/constants';
import { CombinedExtractionData } from '@/types/entities/DataExtraction';
import * as S from './styles';

interface InfoItem {
	label: string;
	value: string;
}

interface GeneralInfoProps {
	extractionData: CombinedExtractionData;
	rowData: {
		id: string;
		organization_id: string;
		company_id: string;
		status?: string;
		originalStatus?: string;
	};
}

export function GeneralInfo({ extractionData, rowData }: Readonly<GeneralInfoProps>): JSX.Element {
	const downloadCSV = useGetDataExtractionCSVToDownload();

	const infoList: InfoItem[] = [
		{
			label: I18n.get('Company'),
			value: extractionData.company_name || '-'
		},
		{
			label: I18n.get('Industrial site'),
			value: extractionData.organization_name || '-'
		},
		{
			label: I18n.get('Sector'),
			value: extractionData.sector_name || '-'
		},
		{
			label: I18n.get('Line'),
			value: extractionData.line_name || '-'
		},
		{
			label: I18n.get('Workstation'),
			value: extractionData.workstation_name || '-'
		},
		{
			label: I18n.get('Reference start date'),
			value: extractionData.startDate ? moment(extractionData.startDate).format('L') : '-'
		},
		{
			label: I18n.get('Reference end date'),
			value: extractionData.endDate ? moment(extractionData.endDate).format('L') : '-'
		},
		{
			label: I18n.get('Author'),
			value: extractionData.author || '-'
		},
		{
			label: I18n.get('Status'),
			value: rowData.status || '-'
		},
		{
			label: I18n.get('Creation date'),
			value: extractionData.createdAt ? moment(extractionData.createdAt).format('L') : '-'
		}
	];

	async function handleDownloadItem(): Promise<void> {
		const data = await downloadCSV.mutateAsync({
			organization_id: rowData.organization_id,
			company_id: rowData.company_id,
			data_extraction_id: rowData.id
		});
		downloadFile(data.url);
	}

	return (
		<S.GeneralInfoContainer>
			<S.HeaderRow justify="space-between" align="middle">
				<Col>
					<S.GeneralInfoTitle>{I18n.get('General information')}</S.GeneralInfoTitle>
				</Col>
				<Col>
					<S.DownloadButton
						onClick={handleDownloadItem}
						type="default"
						disabled={rowData?.originalStatus !== ORIGINAL_STATUS_COMPLETED}
					>
						<DownloadOutlined />
						{I18n.get('Download CSV')}
					</S.DownloadButton>
				</Col>
			</S.HeaderRow>
			<S.InfoListContainer>
				{infoList.map((item) => (
					<S.InfoItem key={item.label}>
						<S.InfoLabel>{item.label}:</S.InfoLabel> {item.value}
					</S.InfoItem>
				))}
			</S.InfoListContainer>
		</S.GeneralInfoContainer>
	);
}
